import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { DetailsSection } from '@/components/ui/features/virtual-events/DetailsSection/DetailsSection';
import { GetStartedWithVirtualEvents } from '@/components/ui/features/virtual-events/GetStarted/GetStartedWithVirtualEvents';
import { VirtualEventsHeading } from '@/components/ui/features/virtual-events/Heading/VirtualEventsHeading';
import { PurpleGradientSection } from '@/components/ui/features/virtual-events/PurpleGradientSection/PurpleGradientSection';
import { ContactUsSection } from '@/components/ui/features/white-glove-service/ContactUsSection/ContactUsSection';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { JsonLd } from '@/components/ui/shared/seo/JsonLd/JsonLd';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryTestimonials } from '@/queries/testimonals';
import { generateStore } from '@/utils/generateStore';
import { buildBreadcrumbSchema, buildWebPageSchema, getAbsoluteUrl } from '@/utils/structured-data';
import { Hydrate } from '@/utils/swr/Hydrate';

const PAGE_PATH = 'events/virtual-events';
const PAGE_DESCRIPTION =
  "Welcome to the future of scientific conferences with Underline's virtual events. Our robust platform is designed to cater to the unique needs of scientific events, offering a cost-effective solution that doesn't compromise on quality or engagement.";
const PAGE_TITLE = 'Professional Platform tailored to Scientific Virtual Events';

const VirtualEvents: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <Layout accessFrom={AccessFrom.Core} navigationVariant="ghost">
        <NextSeo
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          canonical={getAbsoluteUrl(PAGE_PATH)}
          openGraph={{
            url: getAbsoluteUrl(PAGE_PATH),
            title: PAGE_TITLE,
            description: PAGE_DESCRIPTION,
          }}
        />
        <JsonLd
          id="events-virtual"
          schema={[
            buildWebPageSchema({
              path: PAGE_PATH,
              name: PAGE_TITLE,
              description: PAGE_DESCRIPTION,
            }),
            buildBreadcrumbSchema([
              { name: 'Home', path: '' },
              { name: 'Events', path: 'events' },
              { name: 'Virtual Events', path: PAGE_PATH },
            ]),
          ]}
        />
        <VirtualEventsHeading />
        <PurpleGradientSection />
        <DetailsSection />
        <GetStartedWithVirtualEvents />
        <ContactUsSection />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  await store.fetchResources(queryTestimonials());

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default VirtualEvents;
